import { useId } from 'react';

/**
 * The Tabbio wordmark "t", drawn as inline SVG with a violet-to-cyan fill and a
 * sheen that sweeps through the letterform — the same motion as
 * `assets/branding/loader-tabbio.webp`, minus the 83KB download.
 *
 * This is the boot screen: the browser has to paint it *before* the app is
 * ready, so an <Image> here would leave the shell blank until the asset lands.
 * Everything below is inline markup plus one compositor-only transform.
 */

const LOGO_PATH = 'M56.48 2.68002C68.2095 2.23483 82.3058 2.58748 94.1619 2.61566L160.465 2.66058C161.018 12.0569 160.6 25.7727 160.595 35.633C160.492 58.8044 160.544 81.9763 160.754 105.147C184.942 104.934 209.106 105.337 233.32 104.989C234.321 112.393 233.699 122.859 233.661 130.721C233.608 141.643 234.083 153.581 233.488 164.39C209.187 163.454 184.72 164.692 160.706 164.132C160.557 181.636 160.533 199.141 160.636 216.645C160.652 224.291 161.061 237.223 160.42 244.518C182.037 245.031 203.971 244.429 225.586 244.645C232.97 244.72 254.176 244.052 259.66 245.648C261.23 248.549 260.332 278.129 260.291 283.876C260.425 290.654 260.657 297.44 260.237 304.207C259.824 304.22 259.41 304.228 258.996 304.231L210.441 304.266C175.907 304.276 147.51 305.701 120.644 279.781C98.3375 258.26 101.26 236.096 101.313 208.126C101.376 193.457 101.362 178.788 101.27 164.12C97.9301 164.282 93.8017 164.108 90.3931 164.05C60.7908 163.544 30.876 164.666 1.30543 164.087C0.980591 142.803 0.834485 79.003 2.06757 57.3757C2.22311 54.6476 50.8418 5.68761 56.48 2.68002ZM62.7619 65.4198C49.4797 65.2212 36.1955 65.2385 22.9142 65.4713C22.9158 78.2024 22.6029 92.4205 23.0746 105.035L62.8861 105.159C75.6713 105.203 88.4565 105.15 101.24 105C101.452 78.7342 100.616 50.0128 101.383 24.1727C88.5745 24.1941 75.6055 24.1072 62.8109 24.296C62.8246 37.5788 63.1134 52.2316 62.7619 65.4198Z';

// The mark's own viewBox — taller than it is wide, so `size` drives height.
const VIEW_WIDTH = 261;
const VIEW_HEIGHT = 308;
// Narrow enough to read as a travelling band rather than washing the whole
// glyph out. Kept in step with `tabbio-brand-loader-sheen` in global.css,
// which travels VIEW_WIDTH + SHEEN_WIDTH to clear the right edge.
const SHEEN_WIDTH = 110;

type BrandLoaderProps = {
  size?: number;
};

export function BrandLoader({ size = 72 }: BrandLoaderProps) {
  // Gradient/clip ids are document-global; useId keeps two loaders on the same
  // page from stealing each other's defs.
  const uid = useId().replace(/:/g, '');
  const fillId = `tabbio-loader-fill-${uid}`;
  const sheenId = `tabbio-loader-sheen-${uid}`;
  const clipId = `tabbio-loader-clip-${uid}`;

  return (
    <svg
      className="tabbio-brand-loader"
      width={(size * VIEW_WIDTH) / VIEW_HEIGHT}
      height={size}
      viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={fillId} x1="0" y1="0" x2="0.35" y2="1">
          <stop offset="0%" stopColor="#CCC2FF" />
          <stop offset="55%" stopColor="#9B86FD" />
          <stop offset="100%" stopColor="#95E2FC" />
        </linearGradient>
        <linearGradient id={sheenId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0" />
          <stop offset="50%" stopColor="#FFFFFF" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>
        <clipPath id={clipId}>
          <path d={LOGO_PATH} />
        </clipPath>
      </defs>

      <path d={LOGO_PATH} fill={`url(#${fillId})`} />

      <g clipPath={`url(#${clipId})`}>
        <rect
          className="tabbio-brand-loader__sheen"
          x={-SHEEN_WIDTH}
          y="0"
          width={SHEEN_WIDTH}
          height={VIEW_HEIGHT}
          fill={`url(#${sheenId})`}
        />
      </g>
    </svg>
  );
}
